import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Spin, Select } from "antd";
import PurchaseOrderServices from "../../services/purchaseOrder.service";
import "../../assets/css/po2-design.css";

// Task 1900-1903 (Feature 1861 / F3): lists the caller's orders in the new StatusCode
// lifecycle (via Phase 0's MyOrdersByStatus) - a vendor sees their own DRAFT-and-beyond
// orders, a retailer sees only what's been sent to them (PENDING_RETAILER and later). This
// is the "how does anyone find an order" entry point for both the review screen (Task 1902)
// and the detail/history screen (Task 1903) - one list, everyone's own visibility rule
// already enforced server-side.
export const STATUS_LABELS = {
  DRAFT: "Draft",
  PENDING_RETAILER: "Pending Your Review",
  PENDING_VENDOR: "Pending Vendor",
  PO: "Purchase Order",
  INVOICE: "Invoice",
  DISPUTED: "Disputed",
  CASH_CARRY: "Cash & Carry",
  CANCELLED: "Cancelled",
};

// Kept for any other file still importing the old antd Tag colors.
export const STATUS_COLORS = {
  DRAFT: "default",
  PENDING_RETAILER: "gold",
  PENDING_VENDOR: "gold",
  PO: "green",
  INVOICE: "blue",
  DISPUTED: "red",
  CASH_CARRY: "purple",
  CANCELLED: "default",
};

const COMMITTED_STATUSES = ["PO", "INVOICE", "CASH_CARRY", "DISPUTED"];
const OPEN_STATUSES = ["DRAFT", "PENDING_RETAILER", "PENDING_VENDOR"];

// Epic 1857 UI-redesign (Phase 4): restyled to the prototype's vOrders()/rOrders() layout.
// The vendor KPI row (committed value / open pipeline / awaiting retailer / needs review) is
// computed client-side from the already-fetched list - no new backend field needed. Matches
// the prototype's own design choice of showing KPIs only for the vendor, not the retailer,
// list (a retailer's "pipeline" doesn't carry the same meaning against orders they didn't
// write). Columns stay limited to what MyOrdersByStatus actually returns (poId, statusCode,
// referenceNumber, orderTotal, showId, showName) - no invented "buying business"/"units"
// columns from the prototype that this endpoint doesn't provide.
const MyOrders = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const navigate = useNavigate();
  const memberType = useSelector((state) => state.loginReducer?.loginDetials?.responseData?.dealerMemberTypeName);
  const isVendor = memberType === "Vendor" || memberType === "VendorDiamond" || memberType === "Combined";

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilter, setShowFilter] = useState(null);

  useEffect(() => {
    setLoading(true);
    PurchaseOrderServices.GetMyOrdersByStatus()
      .then((res) => {
        setLoading(false);
        if (res?.data?.status) {
          setOrders(res.data.responseData || []);
        }
      })
      .catch(() => setLoading(false));
  }, []);

  // Task 1919 (Feature 1864 / F6): filter options come from shows already present on the
  // caller's own orders, not the "currently active" list (a past order's show may no longer
  // be active) and not a separate endpoint (avoids another round trip for data already here).
  const showOptions = useMemo(() => {
    const seen = new Map();
    orders.forEach((o) => {
      if (o.showId != null && !seen.has(o.showId)) {
        seen.set(o.showId, o.showName || `Show #${o.showId}`);
      }
    });
    return Array.from(seen, ([showId, showName]) => ({ showId, showName }));
  }, [orders]);

  const filteredOrders = showFilter == null ? orders : orders.filter((o) => o.showId === showFilter);

  const kpis = useMemo(() => {
    const sum = (list) => list.reduce((s, o) => s + (Number(o.orderTotal) || 0), 0);
    const committed = orders.filter((o) => COMMITTED_STATUSES.includes(o.statusCode));
    const open = orders.filter((o) => OPEN_STATUSES.includes(o.statusCode));
    return {
      committedValue: sum(committed),
      committedCount: committed.length,
      openPipeline: sum(open),
      awaitingRetailer: orders.filter((o) => o.statusCode === "PENDING_RETAILER").length,
      needsReview: orders.filter((o) => o.statusCode === "PENDING_VENDOR").length,
    };
  }, [orders]);

  return (
    <div className="po2-scope" dir={isRtl ? "rtl" : "ltr"}>
      <div className="po2-phead">
        <div>
          <h1>{t("My Orders")}</h1>
        </div>
        <div className="po2-right">
          {showOptions.length > 0 && (
            <Select
              allowClear
              placeholder={t("Filter by show")}
              style={{ width: 220 }}
              value={showFilter || undefined}
              onChange={(v) => setShowFilter(v ?? null)}
              options={showOptions.map((s) => ({ value: s.showId, label: s.showName }))}
            />
          )}
        </div>
      </div>

      {isVendor && (
        <div className="po2-kpis">
          <div className="po2-kpi">
            <div className="po2-lab">{t("Committed value")}</div>
            <div className="po2-val">${kpis.committedValue.toFixed(2)}</div>
            <div className="po2-sub">{kpis.committedCount} {t("committed orders")}</div>
          </div>
          <div className="po2-kpi">
            <div className="po2-lab">{t("Open pipeline")}</div>
            <div className="po2-val">${kpis.openPipeline.toFixed(2)}</div>
            <div className="po2-sub">{t("written, not yet committed")}</div>
          </div>
          <div className="po2-kpi po2-sec">
            <div className="po2-lab">{t("Awaiting retailer")}</div>
            <div className="po2-val">{kpis.awaitingRetailer}</div>
            <div className="po2-sub">{t("sent, no response yet")}</div>
          </div>
          <div className="po2-kpi po2-sec">
            <div className="po2-lab">{t("Needs your review")}</div>
            <div className="po2-val">{kpis.needsReview}</div>
            <div className="po2-sub">{t("retailer returned changes")}</div>
          </div>
        </div>
      )}

      <div className="po2-panel po2-flush">
        <div className="po2-ph">
          <h3>{filteredOrders.length} {t("orders")}</h3>
        </div>
        <Spin spinning={loading}>
          <div className="po2-tblscroll">
            <table className="po2-dt">
              <thead>
                <tr>
                  <th className="po2-freeze">{t("Order #")}</th>
                  <th>{t("Reference Number")}</th>
                  <th>{t("Status")}</th>
                  <th className="po2-r">{t("Total")}</th>
                  <th>{t("Show")}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.map((row) => (
                  <tr className="po2-click" key={row.poId} onClick={() => navigate(`/orderDetail/${row.poId}`)}>
                    <td className="po2-freeze">
                      <b>{row.poId}</b>
                    </td>
                    <td>{row.referenceNumber || "-"}</td>
                    <td>
                      <span className={`po2-st po2-st-${row.statusCode}`}>{t(STATUS_LABELS[row.statusCode] || row.statusCode)}</span>
                    </td>
                    <td className="po2-r">
                      <b>{row.orderTotal != null ? `$${Number(row.orderTotal).toFixed(2)}` : "-"}</b>
                    </td>
                    <td>{row.showName || "-"}</td>
                    <td>
                      <button className="po2-btn po2-linky" onClick={(e) => { e.stopPropagation(); navigate(`/orderDetail/${row.poId}`); }}>
                        {t("View")}
                      </button>
                    </td>
                  </tr>
                ))}
                {filteredOrders.length === 0 && (
                  <tr>
                    <td colSpan={6}>
                      <div className="po2-empty">
                        <div className="po2-ic">◇</div>
                        <p>{t("No orders yet.")}</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Spin>
      </div>
    </div>
  );
};

export default MyOrders;
